import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type PayoutBody =
  paths["/api/v1/owners/{landlord_id}/payouts"]["post"]["requestBody"]["content"]["application/json"];

const key = (id: string) => ["owners", id] as const;

export function useOwnerStatement(
  landlordId: string | undefined,
  periodStart: string,
  periodEnd: string,
) {
  return useQuery({
    queryKey: [...key(landlordId ?? ""), "statement", periodStart, periodEnd],
    enabled: Boolean(landlordId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/owners/{landlord_id}/statement",
        {
          params: {
            path: { landlord_id: landlordId! },
            query: { period_start: periodStart, period_end: periodEnd },
          },
        },
      );
      if (error || !data) throw new Error("Failed to load statement");
      return data;
    },
  });
}

export function useOwnerPayouts(landlordId: string | undefined) {
  return useQuery({
    queryKey: [...key(landlordId ?? ""), "payouts"],
    enabled: Boolean(landlordId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/owners/{landlord_id}/payouts",
        { params: { path: { landlord_id: landlordId! } } },
      );
      if (error || !data) throw new Error("Failed to load payouts");
      return data;
    },
  });
}

export function useRecordPayout(landlordId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PayoutBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/owners/{landlord_id}/payouts",
        { params: { path: { landlord_id: landlordId } }, body },
      );
      if (error || !data) throw new Error("Failed to record payout");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(landlordId) });
      qc.invalidateQueries({ queryKey: ["ledger"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}
